// src/api/tokenStore.js — in-memory access token store (FORM-0005)
//
// The access token deliberately never touches localStorage or sessionStorage:
// anything persisted there is readable by any script that manages to run on the
// page and survives long after the tab is closed. It lives in this module's
// closure for the lifetime of the tab and is re-minted from the HttpOnly
// refresh cookie on load (see AuthContext) and whenever a request 401s.

let accessToken = null;
const listeners = new Set();

export const CSRF_HEADER = 'X-CSRF-Token';
const CSRF_COOKIE = 'csrf_token';

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || null;
  listeners.forEach((fn) => {
    try {
      fn(accessToken);
    } catch {
      /* a bad listener must not break the auth flow */
    }
  });
}

export function clearAccessToken() {
  setAccessToken(null);
}

// Lets AuthContext mirror the token into React state when the API client
// refreshes it behind the scenes, so components re-render with the new value.
export function subscribeToToken(fn) {
  listeners.add(fn);
  // Braces matter: Set.delete() returns a boolean, and a useEffect destructor
  // must return void, so the caller can do `useEffect(() => subscribe(fn))`.
  return () => {
    listeners.delete(fn);
  };
}

// The CSRF cookie is readable on purpose — the double-submit check needs the
// SPA to echo it back in a header the browser will not send cross-site.
export function readCsrfToken() {
  if (typeof document === 'undefined') return null;
  const prefix = `${CSRF_COOKIE}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return entry ? decodeURIComponent(entry.slice(prefix.length)) : null;
}

/**
 * Header map echoing the CSRF cookie back on cookie-authenticated requests.
 *
 * Annotated as a plain string map: inferred, the two branches give a union
 * (`{ 'X-CSRF-Token': string } | {}`) that is not assignable to `HeadersInit`.
 *
 * @returns {Record<string, string>}
 */
export function csrfHeaders() {
  const token = readCsrfToken();
  return token ? { [CSRF_HEADER]: token } : {};
}

// One-off cleanup of the pre-FORM-0005 persisted token.
export function purgeLegacyStoredToken() {
  try {
    localStorage.removeItem('token');
  } catch {
    /* storage may be unavailable (private mode, blocked cookies) */
  }
}
